/* Manarola 2026 · global.js
   Utilidades, diálogos accesibles, carrito AJAX, header (mega menú y búsqueda) y carruseles.
   Sin dependencias. Cargado con defer en todas las páginas. */
(() => {
  const theme = window.theme || {};

  /* ---------- Utilidades ---------- */
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const formatMoney = (cents, format = theme.moneyFormat || '${{amount}}') => {
    const value = Number(cents) / 100;
    const fmt = (n, dec, thousands = ',', decimal = '.') => {
      const [i, d] = n.toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (d ? decimal + d : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        default: return fmt(value, 2);
      }
    });
  };

  const announce = (msg) => {
    const region = document.getElementById('LiveRegion');
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => { region.textContent = msg; });
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Diálogos (cajones y modales) ---------- */
  const Dialogs = {
    lastTrigger: null,
    open(dialog, trigger) {
      if (!dialog || dialog.open) return;
      this.lastTrigger = trigger || document.activeElement;
      dialog.showModal();
      document.body.classList.add('has-dialog');
      const focusTarget = dialog.querySelector('[autofocus]') || dialog.querySelector('input, button, a[href]');
      if (focusTarget) focusTarget.focus();
      dialog.dispatchEvent(new CustomEvent('dialog:open'));
    },
    close(dialog) {
      if (!dialog || !dialog.open) return;
      dialog.close();
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog) { event.preventDefault(); Dialogs.open(dialog, opener); }
      return;
    }
    const closer = event.target.closest('[data-close-dialog]');
    if (closer) { Dialogs.close(closer.closest('dialog')); return; }
    if (event.target.tagName === 'DIALOG' && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) Dialogs.close(event.target);
    }
  });

  document.addEventListener('close', (event) => {
    if (event.target.tagName !== 'DIALOG') return;
    if (!document.querySelector('dialog[open]')) document.body.classList.remove('has-dialog');
    if (Dialogs.lastTrigger && document.contains(Dialogs.lastTrigger)) Dialogs.lastTrigger.focus();
  }, true);

  /* ---------- Carrito ---------- */
  const Cart = {
    sections() {
      return Array.from(document.querySelectorAll('[data-cart-section]')).map((el) => el.dataset.cartSection)
        .filter((v, i, a) => a.indexOf(v) === i);
    },
    renderSections(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((target) => {
          const source = doc.querySelector(`[data-cart-section="${id}"]`);
          if (source) target.innerHTML = source.innerHTML;
        });
      });
      document.dispatchEvent(new CustomEvent('cart:rendered'));
    },
    async add(formData) {
      const sections = this.sections();
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      this.renderSections(data.sections);
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sections(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      this.renderSections(data.sections);
      return data;
    },
    async update(payload) {
      const res = await fetch(`${theme.routes.cartUpdate}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
      });
      return res.json();
    },
    openDrawer(trigger) {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && theme.cartType === 'drawer') Dialogs.open(drawer, trigger);
      else window.location.href = theme.routes.cart;
    }
  };

  /* Formularios de producto (ficha, tarjetas rápidas) */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-product-form]');
    if (!form) return;
    if (theme.cartType !== 'drawer' || !document.getElementById('CartDrawer')) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const errorEl = form.querySelector('[data-form-error]');
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
    try {
      await Cart.add(new FormData(form));
      announce(theme.strings.added);
      Cart.openDrawer(button);
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      announce(err.message);
    } finally {
      if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); }
    }
  });

  /* Cantidades y eliminar (carrito lateral y página de carrito) */
  const handleQty = debounce(async (input) => {
    const line = Number(input.dataset.line);
    const qty = Math.max(0, Number(input.value));
    const wrapper = input.closest('[data-cart-items]');
    wrapper && wrapper.setAttribute('aria-busy', 'true');
    try { await Cart.change(line, qty); announce(theme.strings.added); }
    catch (err) { announce(err.message); }
    finally { wrapper && wrapper.removeAttribute('aria-busy'); }
  }, 350);

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (step) {
      const input = step.closest('.qty').querySelector('input');
      const min = Number(input.min || 0);
      const next = Math.max(min, Number(input.value) + Number(step.dataset.qtyStep));
      input.value = next;
      if (input.dataset.line) handleQty(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      const input = document.createElement('input');
      input.dataset.line = remove.dataset.cartRemove;
      input.value = 0;
      handleQty(input);
    }
  });
  document.addEventListener('change', (event) => {
    const input = event.target.closest('input[data-line]');
    if (input && event.isTrusted) handleQty(input);
  });

  /* Nota (y atributos) del carrito */
  document.addEventListener('change', (event) => {
    const field = event.target.closest('[data-cart-attribute], [name="note"][data-cart-note]');
    if (!field) return;
    const payload = field.dataset.cartNote !== undefined ? { note: field.value } : { attributes: { [field.dataset.cartAttribute]: field.value } };
    Cart.update(payload);
  });

  /* ---------- Header: mega menú ---------- */
  const header = document.querySelector('[data-header]');
  if (header) {
    const triggers = Array.from(header.querySelectorAll('[data-mega-trigger]'));
    const backdrop = document.querySelector('[data-mega-backdrop]');
    let openTrigger = null;
    let hoverTimer;
    const setHeaderBottom = () => {
      const rect = header.getBoundingClientRect();
      document.documentElement.style.setProperty('--header-bottom', `${Math.max(0, rect.bottom)}px`);
    };
    const closeMega = (focusTrigger = false) => {
      if (!openTrigger) return;
      const panel = document.getElementById(openTrigger.getAttribute('aria-controls'));
      openTrigger.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
      if (backdrop) backdrop.hidden = true;
      if (focusTrigger) openTrigger.focus();
      openTrigger = null;
    };
    const openMega = (trigger) => {
      if (openTrigger === trigger) return;
      closeMega();
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      setHeaderBottom();
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      if (backdrop) backdrop.hidden = false;
      openTrigger = trigger;
    };
    triggers.forEach((trigger) => {
      const item = trigger.closest('[data-mega-item]');
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        trigger.getAttribute('aria-expanded') === 'true' ? closeMega() : openMega(trigger);
      });
      if (window.matchMedia('(hover: hover)').matches) {
        item.addEventListener('mouseenter', () => { clearTimeout(hoverTimer); hoverTimer = setTimeout(() => openMega(trigger), 120); });
        item.addEventListener('mouseleave', () => { clearTimeout(hoverTimer); hoverTimer = setTimeout(() => closeMega(), 200); });
      }
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && openTrigger) closeMega(true); });
    document.addEventListener('click', (e) => { if (openTrigger && !e.target.closest('[data-mega-item]')) closeMega(); });
    header.addEventListener('focusout', (e) => {
      if (openTrigger && e.relatedTarget && !openTrigger.closest('[data-mega-item]').contains(e.relatedTarget)) closeMega();
    });
    if (backdrop) backdrop.addEventListener('click', () => closeMega());
  }

  /* ---------- Búsqueda predictiva ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', debounce(() => this.search(), 300));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; this.input.setAttribute('aria-expanded', 'false'); return; }
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[limit]=6&resources[type]=product,collection,page&section_id=predictive-search`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        const content = doc.querySelector('[data-predictive-content]');
        this.results.innerHTML = content ? content.innerHTML : '';
        const count = this.results.querySelectorAll('[role="option"]').length;
        this.input.setAttribute('aria-expanded', count > 0 ? 'true' : 'false');
        if (this.status) this.status.textContent = this.results.querySelector('[data-predictive-count]')?.textContent || '';
      } catch (e) { /* sin conexión: la búsqueda normal sigue funcionando con Enter */ }
    }
    onKey(e) {
      const options = Array.from(this.results.querySelectorAll('[role="option"]'));
      if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      const current = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (e.key === 'Enter') {
        if (current > -1) { e.preventDefault(); options[current].querySelector('a')?.click(); }
        return;
      }
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? (current + 1) % options.length : (current - 1 + options.length) % options.length;
      options.forEach((o) => o.setAttribute('aria-selected', 'false'));
      options[next].setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest' });
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Carrusel accesible ---------- */
  class SlideShow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.pauseBtn = this.querySelector('[data-pause]');
      this.mode = this.dataset.mode || 'scroll'; // scroll | fade
      this.index = 0;
      this.interval = Number(this.dataset.autoplay || 0) * 1000;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.paused = this.reduced || this.interval === 0;
      if (this.slides.length < 2) return;
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => { this.goTo(i, true); }));
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.goTo(this.index - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.goTo(this.index + 1, true));
      if (this.pauseBtn) {
        this.pauseBtn.addEventListener('click', () => { this.userPaused = !this.paused; this.paused ? this.play() : this.stop(); });
        if (this.reduced || this.interval === 0) this.pauseBtn.hidden = true;
      }
      if (this.mode === 'scroll' && this.track) {
        this.track.addEventListener('scroll', debounce(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.setActive(Math.min(this.slides.length - 1, Math.max(0, i)));
        }, 80), { passive: true });
      }
      this.addEventListener('mouseenter', () => this.hold(true));
      this.addEventListener('mouseleave', () => this.hold(false));
      this.addEventListener('focusin', () => this.hold(true));
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.hold(false); });
      this.setActive(0);
      if (!this.paused) this.play();
    }
    hold(on) { this.holding = on; }
    play() {
      this.paused = false;
      this.updatePauseBtn();
      clearInterval(this.timer);
      this.timer = setInterval(() => { if (!this.holding && document.visibilityState === 'visible') this.goTo(this.index + 1); }, this.interval);
    }
    stop() { this.paused = true; clearInterval(this.timer); this.updatePauseBtn(); }
    updatePauseBtn() {
      if (!this.pauseBtn) return;
      this.pauseBtn.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
      this.pauseBtn.setAttribute('aria-label', this.paused ? this.pauseBtn.dataset.labelPlay : this.pauseBtn.dataset.labelPause);
      this.pauseBtn.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', this.paused);
      this.pauseBtn.querySelector('[data-icon-play]')?.toggleAttribute('hidden', !this.paused);
    }
    goTo(i, user = false) {
      const n = this.slides.length;
      const next = (i + n) % n;
      if (user && !this.paused) this.play();
      if (this.mode === 'scroll' && this.track) {
        this.track.scrollTo({ left: this.track.clientWidth * next, behavior: this.reduced ? 'auto' : 'smooth' });
      }
      this.setActive(next);
    }
    setActive(i) {
      this.index = i;
      this.slides.forEach((s, k) => {
        const active = k === i;
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
        s.toggleAttribute('inert', !active && this.mode === 'fade');
        s.classList.toggle('is-active', active);
      });
      this.dots.forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
    }
  }
  if (!customElements.get('slide-show')) customElements.define('slide-show', SlideShow);

  /* ---------- Aparición suave ---------- */
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); } });
    }, { rootMargin: '0px 0px -10% 0px' });
    document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
  } else {
    document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------- Guía de tallas (contenido por producto) ---------- */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-size-guide]');
    if (!btn) return;
    const modal = document.getElementById('SizeGuideModal');
    const source = document.getElementById(btn.dataset.sizeGuide);
    if (!modal || !source) return;
    event.preventDefault();
    modal.querySelector('[data-size-guide-content]').innerHTML = source.innerHTML;
    Dialogs.open(modal, btn);
  });

  window.Manarola = { Cart, Dialogs, formatMoney, announce, debounce, parseHTML };
})();

/* Pausa de la frase en movimiento (WCAG 2.2.2) */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-marquee-toggle]');
  if (!btn) return;
  const section = btn.closest('.marquee');
  const paused = section.classList.toggle('is-paused');
  btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
});
